import { PORTAL_PROP_ID, PORTAL_REACH, TELEPORT_COMBAT_LOCK_MS, TELEPORT_COST, } from '../data/portals.js';
import { footprintOf } from '../data/props.js';
import { respawnPoint } from '../data/zones.js';
/**
 * Travel between the villages' portals (ADR 0017). Pure: the server applies
 * a journey, the client greys out the ones it would refuse with the same
 * functions.
 */
export function portalById(zone, id) {
    return zone.portals.find((portal) => portal.id === id);
}
/**
 * Within `PORTAL_REACH` tiles of the arch, diagonals included, measured to
 * the nearest tile of its footprint: one may use it from either side.
 */
export function withinPortalReach(at, portal) {
    const box = footprintOf({ prop: PORTAL_PROP_ID, x: portal.x, y: portal.y });
    const dx = Math.max(box.x0 - at.x, 0, at.x - box.x1);
    const dy = Math.max(box.y0 - at.y, 0, at.y - box.y1);
    return Math.max(dx, dy) <= PORTAL_REACH;
}
/** The portal a player stands at, if any. */
export function portalInReach(zone, at) {
    return zone.portals.find((portal) => withinPortalReach(at, portal));
}
/** Gold for a journey to this portal: priced by its village's biome. */
export function teleportCost(portal) {
    return TELEPORT_COST[portal.biome];
}
/**
 * Milliseconds before a fighter may travel again: `TELEPORT_COMBAT_LOCK_MS`
 * after their last blow dealt or taken, 0 when they are out of combat.
 */
export function combatLockRemaining(lastCombatAt, now) {
    if (lastCombatAt === null)
        return 0;
    return Math.max(0, lastCombatAt + TELEPORT_COMBAT_LOCK_MS - now);
}
/**
 * Why a journey is refused, or null when it may go: the portal must exist,
 * have been used once, not be the one the player stands at, and be paid for.
 */
export function teleportRefusal(zone, request) {
    const portal = portalById(zone, request.portalId);
    if (portal === undefined)
        return 'unknown-portal';
    if (!request.discovered.has(portal.id))
        return 'undiscovered';
    if (withinPortalReach(request.at, portal))
        return 'here';
    if (request.gold < teleportCost(portal))
        return 'missing-gold';
    return null;
}
/**
 * Tiles a traveller may land on at a portal: within reach of the arch, in
 * front of it or beside it, never on it nor behind it (the stone would hide
 * them), and walkable. The tile in front of the arch is always one of them
 * when it is walkable, which the zone guarantees.
 */
export function landingTiles(portal, isWalkable) {
    const box = footprintOf({ prop: PORTAL_PROP_ID, x: portal.x, y: portal.y });
    const tiles = [];
    for (let y = portal.y; y <= box.y1 + PORTAL_REACH; y += 1) {
        for (let x = box.x0 - PORTAL_REACH; x <= box.x1 + PORTAL_REACH; x += 1) {
            const onArch = x >= box.x0 && x <= box.x1 && y >= box.y0 && y <= box.y1;
            if (!onArch && isWalkable(x, y))
                tiles.push({ x, y });
        }
    }
    return tiles;
}
/**
 * Where a traveller lands this time: a tile drawn among `landingTiles`, so
 * each arrival is somewhere else around the arch and two players arriving
 * together seldom stand on each other. The arch's own arrival tile when
 * nothing else is open.
 */
export function landingTile(portal, isWalkable, rng) {
    const tiles = landingTiles(portal, isWalkable);
    return tiles[Math.floor(rng() * tiles.length)] ?? portal.arrival;
}
/**
 * Where a fallen player gets back up: around the portal they chose, drawn
 * like a journey's landing, or the zone's own shrine when they chose none,
 * or one this zone does not have.
 */
export function respawnTile(zone, respawnPortal, isWalkable, rng) {
    const portal = respawnPortal === null ? undefined : portalById(zone, respawnPortal);
    return portal === undefined ? respawnPoint(zone) : landingTile(portal, isWalkable, rng);
}
/** How far from the aimed tile a game master's teleport looks for open ground (ADR 0025). */
export const ADMIN_TELEPORT_SEARCH = 12;
/**
 * The open tile nearest to `target`, by rings of growing Chebyshev distance
 * (a click on a tree lands beside it), or undefined when nothing within
 * `radius` is walkable. Within a ring, the straight distance decides, then
 * reading order, so the answer never depends on luck.
 */
export function nearestOpenTile(target, isWalkable, radius = ADMIN_TELEPORT_SEARCH) {
    for (let ring = 0; ring <= radius; ring += 1) {
        let best;
        let bestDistance = Infinity;
        for (let dy = -ring; dy <= ring; dy += 1) {
            for (let dx = -ring; dx <= ring; dx += 1) {
                if (Math.max(Math.abs(dx), Math.abs(dy)) !== ring)
                    continue;
                const x = target.x + dx;
                const y = target.y + dy;
                if (x < 0 || y < 0 || !isWalkable(x, y))
                    continue;
                const distance = dx * dx + dy * dy;
                if (distance < bestDistance) {
                    best = { x, y };
                    bestDistance = distance;
                }
            }
        }
        if (best !== undefined)
            return best;
    }
    return undefined;
}
//# sourceMappingURL=travel.js.map